import {
  Select,
  Input,
  InputNumber,
  DatePicker,
} from "antd";
import TextArea from "antd/lib/input/TextArea";
import { useContext, useEffect, useState } from "react";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import {
  newClientEscalation,
  buildSortedOptions,
} from "../../../utils/Constants";
import { useDispatch } from "react-redux";
import secureLocalStorage from "react-secure-storage";
import dayjs from "dayjs";

import {
  fetchUsersThunk,
} from "../../../store/thunks/usersThunk";

import {
  fetchProcessesByClientThunk,
} from "../../../store/thunks/processesThunk";

import {
  fetchClientsByDeptThunk,
} from "../../../store/thunks/clientsThunk";

import {
  fetchDepartmentsSimpleThunk,
} from "../../../store/thunks/departmentsThunk";

import {
  fetchDashboardUsersThunk,
} from "../../../store/thunks/dashboardThunk";

import "./newClientEscalation.css";

const onSearch = () => {};

const EditClientEscalation = ({ editingRecord }) => {
  const dispatch = useDispatch();

  const { globalState, setGlobalState } =
    useContext(GlobalStateContext);

  const [departments, setDepartments] = useState([]);
  const [clients, setClients] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [reportingTLs, setReportingTLs] = useState([]);

  const slsRole = secureLocalStorage.getItem("role");
  const slsLIUId = secureLocalStorage.getItem("lIUId");

  /*
   * ============================================================
   * HELPERS
   * ============================================================
   */

  const getName = (value) => {
    if (!value) return "";

    if (typeof value === "object") {
      return value?.name || "";
    }

    return value;
  };

  const getId = (value) => {
    if (!value) return null;

    if (typeof value === "object") {
      return value?._id || null;
    }

    return value;
  };

  const isValidObjectId = (value) => {
    if (!value || typeof value !== "string") {
      return false;
    }

    return /^[a-f\d]{24}$/i.test(value);
  };

  const findOptionByName = (
    options,
    value,
  ) => {
    if (!value || !Array.isArray(options)) {
      return null;
    }

    const name = getName(value)
      ?.trim()
      .toLowerCase();

    return (
      options.find(
        (option) =>
          option?.label
            ?.trim()
            .toLowerCase() === name,
      ) || null
    );
  };

  /*
   * ============================================================
   * FETCH DEPARTMENTS
   * ============================================================
   */

  const fetchDepartments = async () => {
    try {
      const response = await dispatch(
        fetchDepartmentsSimpleThunk({
          route: "departments",
          action: "findAll",
        }),
      ).unwrap();

      const options =
        response?.map((department) => ({
          label: department?.name,
          value: department?._id,
        })) || [];

      setDepartments(options);

      return options;
    } catch (error) {
      console.error(
        "Error fetching departments:",
        error,
      );

      setDepartments([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH CLIENTS BY DEPARTMENT
   * ============================================================
   */

  const fetchClientsByDepartment = async (
    departmentId,
  ) => {
    if (
      !departmentId ||
      !isValidObjectId(departmentId)
    ) {
      console.error(
        "Invalid department ID:",
        departmentId,
      );

      setClients([]);

      return [];
    }

    try {
      console.log(
        "Fetching clients for department ID:",
        departmentId,
      );

      const response = await dispatch(
        fetchClientsByDeptThunk({
          department: departmentId,
        }),
      ).unwrap();

      const options =
        response?.map((client) => ({
          label: client?.name,
          value: client?._id,
        })) || [];

      setClients(options);

      return options;
    } catch (error) {
      console.error(
        "Error fetching clients:",
        error,
      );

      setClients([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH PROCESSES BY CLIENT + DEPARTMENT
   * ============================================================
   */

  const fetchProcessesByClient = async (
    clientId,
    departmentId,
  ) => {
    if (
      !clientId ||
      !departmentId ||
      !isValidObjectId(clientId) ||
      !isValidObjectId(departmentId)
    ) {
      console.error(
        "Invalid process dependency IDs:",
        {
          clientId,
          departmentId,
        },
      );

      setProcesses([]);

      return [];
    }

    try {
      console.log(
        "Fetching processes:",
        {
          clientId,
          departmentId,
        },
      );

      const response = await dispatch(
        fetchProcessesByClientThunk({
          client: clientId,
          department: departmentId,
        }),
      ).unwrap();

      const options =
        response?.map((process) => ({
          label: process?.name,
          value: process?._id,
        })) || [];

      setProcesses(options);

      return options;
    } catch (error) {
      console.error(
        "Error fetching processes:",
        error,
      );

      setProcesses([]);

      return [];
    }
  };

  /*
   * ============================================================
   * FETCH REPORTING TL BY DEPARTMENT
   * ============================================================
   */

  const fetchReportingTLByDepartment = async (
    departmentId,
  ) => {
    if (
      !departmentId ||
      !isValidObjectId(departmentId)
    ) {
      console.error(
        "Invalid Reporting TL department ID:",
        departmentId,
      );

      setReportingTLs([]);

      return [];
    }

    try {
      const payload = {
        route: "users",
        action: "findAll",
        fields: ["tl", "atl"],
        role: slsRole,
        lIUId: slsLIUId,
        departments: [departmentId],
      };

      const result = await dispatch(
        fetchDashboardUsersThunk(payload),
      ).unwrap();

      const users = Array.isArray(result)
        ? result
        : [];

      const tlUsers = users.filter(
        (user) =>
          user?.role ===
            process.env.REACT_APP_TLROLE ||
          user?.role ===
            process.env.REACT_APP_ATLROLE,
      );

      const options = buildSortedOptions(
        tlUsers,
        "name",
        "_id",
        true,
        [
          process.env.REACT_APP_TLROLE?.toLowerCase(),
          process.env.REACT_APP_ATLROLE?.toLowerCase(),
        ],
      );

      setReportingTLs(options);

      return options;
    } catch (error) {
      console.error(
        "Error fetching Reporting TL:",
        error,
      );

      setReportingTLs([]);

      return [];
    }
  };

  /*
   * ============================================================
   * GET AGENT / AUDITOR OPTIONS
   *
   * We keep this separate because those options are normally
   * already available from the field configuration / API.
   * ============================================================
   */

  const getFieldOptions = (
    field,
    currentOptions,
  ) => {
    if (
      field.key === "eDepartmentName"
    ) {
      return departments;
    }

    if (field.key === "eClientName") {
      return clients;
    }

    if (field.key === "eProcessName") {
      return processes;
    }

    if (
      field.key ===
      "eReportingTlName"
    ) {
      return reportingTLs;
    }

    return currentOptions || [];
  };

  /*
   * ============================================================
   * INITIAL EDIT LOAD
   *
   * IMPORTANT:
   *
   * 1. Load departments
   * 2. Find department ID
   * 3. Load clients
   * 4. Find client ID
   * 5. Load processes
   * 6. Find process ID
   * 7. Load Reporting TL
   * 8. Find Reporting TL ID
   * 9. Populate complete globalState
   * ============================================================
   */


  /*
   * ============================================================
   * SELECT CHANGE
   * ============================================================
   */

  const handleSelectChange = (
    field,
    value,
    option,
  ) => {
    setGlobalState((prev) => ({
      ...prev,

      [`${field.key}Val`]:
        option?.label || null,

      [field.key]:
        value || field.errorMessage,

      /*
       * Department changed
       */
      ...(field.key ===
        "eDepartmentName" && {
        eClientName: null,
        eClientNameVal: null,

        eProcessName: null,
        eProcessNameVal: null,

        eReportingTlName: null,
        eReportingTlNameVal: null,
      }),

      /*
       * Client changed
       */
      ...(field.key ===
        "eClientName" && {
        eProcessName: null,
        eProcessNameVal: null,
      }),
    }));

    /*
     * Department
     */
    if (
      field.key ===
      "eDepartmentName"
    ) {
      fetchClientsByDepartment(value);

      fetchReportingTLByDepartment(
        value,
      );
    }

    /*
     * Client
     */
    if (
      field.key ===
      "eClientName"
    ) {
      fetchProcessesByClient(
        value,
        globalState?.eDepartmentName,
      );
    }
  };

  /*
   * ============================================================
   * RENDER
   * ============================================================
   */

  return (
    <div id="antdformstyle">
      {newClientEscalation.map(
        (section) => (
          <div key={section.key}>
            <div className="pb-16 cusFormTitle">
              {section.title}
            </div>

            <div className="details-container plr-16">
              {section.fields
                .reduce(
                  (
                    rows,
                    field,
                    index,
                  ) => {
                    if (
                      index % 2 ===
                      0
                    ) {
                      rows.push(
                        section.fields.slice(
                          index,
                          index + 2,
                        ),
                      );
                    }

                    return rows;
                  },
                  [],
                )
                .map(
                  (
                    fieldPair,
                    pairIndex,
                  ) => (
                    <div
                      className="details-wrap"
                      key={
                        pairIndex
                      }
                    >
                      {fieldPair.map(
                        (field) => (
                          <div
                            key={
                              field.key
                            }
                            className="details-inner"
                          >
                            <label className="formCusLabel">
                              {
                                field.label
                              }

                              {field.required && (
                                <span className="reqred">
                                  *
                                </span>
                              )}
                            </label>

                            {/* ================================================= */}
                            {/* INPUT */}
                            {/* ================================================= */}

                            {field.type ===
                              "input" && (
                              <Input
                                placeholder={
                                  field.placeholder
                                }
                                value={
                                  globalState?.[
                                    `${field.key}Val`
                                  ] ?? ""
                                }
                                onChange={(
                                  e,
                                ) =>
                                  setGlobalState(
                                    (
                                      prev,
                                    ) => ({
                                      ...prev,

                                      [`${field.key}Val`]:
                                        e
                                          .target
                                          .value,

                                      [field.key]:
                                        e
                                          .target
                                          .value ||
                                        field.errorMessage,
                                    }),
                                  )
                                }
                              />
                            )}

                            {/* ================================================= */}
                            {/* NUMBER */}
                            {/* ================================================= */}

                            {field.type ===
                              "number" && (
                              <InputNumber
                                style={{
                                  width:
                                    "100%",
                                }}
                                placeholder={
                                  field.placeholder
                                }
                                value={
                                  globalState?.[
                                    `${field.key}Val`
                                  ] ?? null
                                }
                                onChange={(
                                  value,
                                ) =>
                                  setGlobalState(
                                    (
                                      prev,
                                    ) => ({
                                      ...prev,

                                      [`${field.key}Val`]:
                                        value,

                                      [field.key]:
                                        value ||
                                        field.errorMessage,
                                    }),
                                  )
                                }
                              />
                            )}

                            {/* ================================================= */}
                            {/* READONLY */}
                            {/* ================================================= */}

                            {field.type ===
                              "readonly" && (
                              <div className="readonly-field">
                                <span>
                                  {
                                    globalState?.[
                                      `${field.key}Val`
                                    ]
                                  }
                                </span>
                              </div>
                            )}

                            {/* ================================================= */}
                            {/* DATE */}
                            {/* ================================================= */}

                            {field.type ===
                              "date" && (
                              <DatePicker
                                style={{
                                  width:
                                    "100%",
                                }}
                                placeholder={
                                  field.placeholder
                                }
                                value={
                                  globalState?.[
                                    `${field.key}Val`
                                  ] &&
                                  dayjs(
                                    globalState?.[
                                      `${field.key}Val`
                                    ],
                                  ).isValid()
                                    ? dayjs(
                                        globalState?.[
                                          `${field.key}Val`
                                        ],
                                      )
                                    : null
                                }
                                disabledDate={(
                                  current,
                                ) => {
                                  /*
                                   * Transaction Date
                                   * cannot be after
                                   * Error Raise Date
                                   */
                                  if (
                                    field.key ===
                                      "eTransactionDate" &&
                                    globalState?.eErrorRaiseDateVal
                                  ) {
                                    return current.isAfter(
                                      dayjs(
                                        globalState.eErrorRaiseDateVal,
                                      ),
                                      "day",
                                    );
                                  }

                                  return false;
                                }}
                                onChange={(
                                  date,
                                ) => {
                                  const dayName =
                                    date
                                      ? date.format(
                                          "ddd",
                                        )
                                      : "";

                                  setGlobalState(
                                    (
                                      prev,
                                    ) => ({
                                      ...prev,

                                      [`${field.key}Val`]:
                                        date,

                                      [field.key]:
                                        date ||
                                        field.errorMessage,

                                      ...(field.key ===
                                        "eTransactionDate" && {
                                        eDayoftheweekVal:
                                          dayName ||
                                          null,

                                        eDayoftheweek:
                                          dayName ||
                                          null,
                                      }),
                                    }),
                                  );
                                }}
                              />
                            )}

                            {/* ================================================= */}
                            {/* TEXTAREA */}
                            {/* ================================================= */}

                            {field.type ===
                              "textarea" && (
                              <TextArea
                                placeholder={
                                  field.placeholder
                                }
                                value={
                                  globalState?.[
                                    `${field.key}Val`
                                  ] ?? ""
                                }
                                onChange={(
                                  e,
                                ) =>
                                  setGlobalState(
                                    (
                                      prev,
                                    ) => ({
                                      ...prev,

                                      [`${field.key}Val`]:
                                        e
                                          .target
                                          .value,

                                      [field.key]:
                                        e
                                          .target
                                          .value ||
                                        field.errorMessage,
                                    }),
                                  )
                                }
                              />
                            )}

                            {/* ================================================= */}
                            {/* SELECT */}
                            {/* ================================================= */}

                            {(
  field.type === "select" ||
  [
    "eDepartmentName",
    "eClientName",
    "eProcessName",
    "eReportingTlName",
  ].includes(field.key)
) && (
  <Select
    showSearch
    allowClear
    placeholder={field.placeholder}
    optionFilterProp="label"
    value={globalState?.[field.key] || null}
    onChange={(value, option) =>
      handleSelectChange(
        field,
        value,
        option,
      )
    }
    filterOption={(input, option) =>
      (option?.label || "")
        .toLowerCase()
        .includes(input.toLowerCase())
    }
    options={
      field.key === "eDepartmentName"
        ? departments
        : field.key === "eClientName"
          ? clients
          : field.key === "eProcessName"
            ? processes
            : field.key === "eReportingTlName"
              ? reportingTLs
              : field.options
    }
    disabled={
      field.key === "eClientName"
        ? !globalState?.eDepartmentName
        : field.key === "eProcessName"
          ? !globalState?.eClientName
          : field.key === "eReportingTlName"
            ? !globalState?.eDepartmentName
            : false
    }
  />
)}

                            {/* ================================================= */}
                            {/* VALIDATION ERROR */}
                            {/* ================================================= */}

                            {globalState?.[
                              field.key
                            ] ===
                              field.errorMessage && (
                              <div className="error">
                                {
                                  field.errorMessage
                                }
                              </div>
                            )}
                          </div>
                        ),
                      )}
                    </div>
                  ),
                )}
            </div>
          </div>
        ),
      )}
    </div>
  );
};

export default EditClientEscalation;